import { injectable, inject } from 'inversify';
import { TYPES } from '../di/types';
import { Address } from '../models/address.model';
import { apiClient } from './NativeApiClient';

export interface StaticMapMarker {
  size: string;
  color: string;
  address: Address;
}

type AutocompleteSuggestion = string;

export interface GeocodeService {
  staticMap(
    center: Address,
    height: number,
    width: number,
    styles?: string[],
    markers?: StaticMapMarker[],
  ): Promise<string>;
  geocode(addressText: string): Promise<Address>;
  autocomplete(queryText: string): Promise<AutocompleteSuggestion[]>;
  getCurrentPosition(): Promise<Address>;
}

@injectable()
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class GeocodeServiceImpl implements GeocodeService {
  public geoCapable: boolean;

  constructor(
    @inject(TYPES.Window) private readonly windowInstance: Window,
    @inject(TYPES.AppConfig) private readonly appConfig: AppConfig,
  ) {
    this.geoCapable = !!(
      this.windowInstance.navigator && this.windowInstance.navigator.geolocation
    );
  }

  private getStaticAddressString(address: Address): string {
    if (address.longitude && address.latitude) {
      return `${address.latitude},${address.longitude}`;
    }

    return address
      .toFullLabel()
      .replace(new RegExp(', ', 'g'), ',')
      .replace(new RegExp(' ', 'g'), '+');
  }

  private getMarkerString(marker: StaticMapMarker): string {
    return (
      'size:' +
      marker.size +
      '|' +
      'color:' +
      marker.color +
      '|' +
      this.getStaticAddressString(marker.address)
    );
  }

  public async staticMap(
    center: Address,
    height: number,
    width: number,
    styles?: string[],
    markers?: StaticMapMarker[],
  ): Promise<string> {
    const paramStrings: string[] = [];
    paramStrings.push(
      'center=' + encodeURI(this.getStaticAddressString(center)),
      'size=' + width + 'x' + height,
    );

    if (styles) {
      styles.forEach((style: string) => {
        paramStrings.push('style=' + encodeURI(style));
      });
    }

    if (markers) {
      markers.forEach((marker: StaticMapMarker) => {
        paramStrings.push('markers=' + encodeURI(this.getMarkerString(marker)));
      });
    }

    let response: any;
    try {
      response = await apiClient.get<{ url: string }>(
        `/geocode/staticmap/sign?${paramStrings.join('&')}`,
      );
      return response.url;
    } catch (e) {
      console.warn(e);
      return '';
    }
  }

  public async geocode(addressText: string): Promise<Address> {
    let response: any;
    try {
      const results = await apiClient.get<any[]>(
        `/geocode?address=${encodeURIComponent(addressText)}`,
      );
      response = results[0];
    } catch (e) {
      console.warn(e);
      response = null;
    }

    return new Address(response);
  }

  public async autocomplete(
    queryText: string,
  ): Promise<AutocompleteSuggestion[]> {
    let response: any;
    try {
      response = await apiClient.get<AutocompleteSuggestion[]>(
        `/geocode/autocomplete?queryText=${encodeURIComponent(queryText)}`,
      );
    } catch (e) {
      console.warn(e);
      response = [];
    }

    return response;
  }

  public async getCurrentPosition(): Promise<Address> {
    let response: any;
    try {
      // Get coordinates from browser geolocation
      const position = await new Promise<any>((resolve, reject) => {
        if (!this.geoCapable) {
          reject(new Error('Geolocation not supported'));
          return;
        }
        this.windowInstance.navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
        );
      });

      // Call reverse geocode endpoint
      const results = await apiClient.get<any[][]>(
        `/geocode/reverse?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}`,
      );
      response = results[0][0];
    } catch (e) {
      console.warn(e);
      response = null;
    }

    return new Address(response);
  }
}
